import { useEffect, useMemo, useRef } from "react";
import { formatTimeLabel } from "../utils/datetime";
import { hexToRgba, type ChartConfig, type ChartKey } from "../utils/settings";
import type { ChartPoint } from "../utils/types";

const DEFAULT_TOOLTIP_BG = "rgba(10, 14, 20, 0.92)";
const DEFAULT_TOOLTIP_TEXT = "#e6f0ff";
const DEFAULT_TOOLTIP_BORDER = "rgba(207, 225, 255, 0.18)";
const DEFAULT_GRID_COLOR = "rgba(207, 225, 255, 0.10)";
const DEFAULT_AXIS_TEXT = "rgba(207, 225, 255, 0.85)";

const HOVER_THROTTLE_MS = 50;

// チャート描画用のoptions/data/hover同期などをまとめて生成するhook。
export const useBpsLogBitrateCharts = (args: {
  isRealtime: boolean;
  realtimeWindowMinutes: number;
  realtimeAxisEndMs: number | null;
  realtimeNowMs: number;
  realtimeRightEdgeFallbackLagMs: number;
  periodAxis: { startMs: number; endMs: number } | null;
  showDateOnXTicks: boolean;
  bitrateYMax: number;
  stepSize: number;
  handleZoomOrPanComplete: (chart: any) => void;
  forceLatestRef: React.MutableRefObject<boolean>;
  pendingHoverIdxRef: React.MutableRefObject<number | null>;
  hoverRafRef: React.MutableRefObject<number | null>;
  suppressHoverUntilMsRef: React.MutableRefObject<number>;
  setHoveredIdx: React.Dispatch<React.SetStateAction<number | null>>;
  chartConfigs: ChartConfig[];
  chartPoints: ChartPoint[];
  getSeriesY: (p: ChartPoint, key: ChartKey) => number | null;
}) => {
  const {
    isRealtime,
    realtimeWindowMinutes,
    realtimeAxisEndMs,
    realtimeNowMs,
    realtimeRightEdgeFallbackLagMs,
    periodAxis,
    showDateOnXTicks,
    bitrateYMax,
    stepSize,
    handleZoomOrPanComplete,
    forceLatestRef,
    pendingHoverIdxRef,
    hoverRafRef,
    suppressHoverUntilMsRef,
    setHoveredIdx,
    chartConfigs,
    chartPoints,
    getSeriesY,
  } = args;

  const hoverThrottleTimeoutRef = useRef<number | null>(null);
  const lastHoverCommitMsRef = useRef<number>(0);

  useEffect(() => {
    return () => {
      if (hoverThrottleTimeoutRef.current !== null) {
        window.clearTimeout(hoverThrottleTimeoutRef.current);
        hoverThrottleTimeoutRef.current = null;
      }
    };
  }, []);

  const handleZoomOrPanCompleteRef = useRef(handleZoomOrPanComplete);
  useEffect(() => {
    handleZoomOrPanCompleteRef.current = handleZoomOrPanComplete;
  }, [handleZoomOrPanComplete]);

  const realtimeAxis = useMemo(() => {
    if (!isRealtime) return null;
    const windowSec = Math.max(1, Math.floor(realtimeWindowMinutes * 60));
    const endMs =
      typeof realtimeAxisEndMs === "number"
        ? realtimeAxisEndMs
        : Math.floor((realtimeNowMs - realtimeRightEdgeFallbackLagMs) / 1000) *
          1000;
    const startMs = endMs - (windowSec - 1) * 1000;
    return { startMs, endMs };
  }, [
    isRealtime,
    realtimeAxisEndMs,
    realtimeNowMs,
    realtimeRightEdgeFallbackLagMs,
    realtimeWindowMinutes,
  ]);

  const periodAxisSafe = useMemo(() => {
    if (isRealtime) return null;
    if (!periodAxis) return null;
    const a = Number(periodAxis.startMs);
    const b = Number(periodAxis.endMs);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    const min = Math.min(a, b);
    const max = Math.max(a, b);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return null;
    const span = Math.max(1000, max - min);
    return { startMs: min, endMs: min + span };
  }, [isRealtime, periodAxis]);

  const bitrateChartOptions = useMemo(() => {
    return {
      responsive: true,
      animation: isRealtime ? (false as const) : { duration: 140 },
      maintainAspectRatio: false,
      layout: {
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      interaction: {
        mode: "nearest" as const,
        intersect: false,
        axis: "x" as const,
      },
      hover: {
        mode: "nearest" as const,
        intersect: false,
      },
      elements: {
        line: { tension: 0 },
      },
      plugins: {
        legend: { display: false },
        title: { display: false },
        tooltip: {
          enabled: false,
          backgroundColor: DEFAULT_TOOLTIP_BG,
          titleColor: DEFAULT_TOOLTIP_TEXT,
          bodyColor: DEFAULT_TOOLTIP_TEXT,
          borderColor: DEFAULT_TOOLTIP_BORDER,
          borderWidth: 1,
          callbacks: {
            title: (items: any[]) => {
              const first = Array.isArray(items) ? items[0] : null;
              const xRaw = first?.parsed?.x ?? first?.raw?.x ?? null;
              const ms = Number(xRaw);
              if (!Number.isFinite(ms)) return "";
              return formatTimeLabel(new Date(ms).toISOString(), true, true);
            },
          },
        },
        crosshair: {
          activeXMs: null,
          color: "#ff9800",
          lineWidth: 1,
        },
        zoom: {
          pan: {
            enabled: false,
          },
          zoom: {
            wheel: {
              enabled: !isRealtime,
            },
            pinch: {
              enabled: !isRealtime,
            },
            drag: {
              enabled: !isRealtime,
            },
            mode: "x" as const,
            onZoomComplete: ({ chart }: any) =>
              handleZoomOrPanCompleteRef.current(chart),
          },
        },
      },
      scales: {
        x: {
          type: "linear" as const,
          min: realtimeAxis
            ? realtimeAxis.startMs
            : periodAxisSafe
              ? periodAxisSafe.startMs
              : undefined,
          max: realtimeAxis
            ? realtimeAxis.endMs
            : periodAxisSafe
              ? periodAxisSafe.endMs
              : undefined,
          display: true,
          title: {
            display: true,
            padding: { top: 0, bottom: 0 },
            color: DEFAULT_AXIS_TEXT,
            font: { size: 11 },
          },
          ticks: {
            maxTicksLimit: 8,
            maxRotation: 0,
            minRotation: 0,
            autoSkip: true,
            color: DEFAULT_AXIS_TEXT,
            font: { size: 10 },
            callback: (v: any, idx: number, ticks: any[]) => {
              if (Array.isArray(ticks) && idx === ticks.length - 1) {
                return "";
              }
              const ms = Number(v);
              if (!Number.isFinite(ms)) return "";
              return formatTimeLabel(
                new Date(ms).toISOString(),
                showDateOnXTicks,
                true,
              );
            },
          },
          grid: {
            color: DEFAULT_GRID_COLOR,
          },
        },
        y: {
          min: 0,
          max: bitrateYMax,
          title: {
            display: true,
            text: "Mbps",
            padding: { top: 0, bottom: 0 },
            color: DEFAULT_AXIS_TEXT,
            font: { size: 11 },
          },
          ticks: {
            stepSize,
            color: DEFAULT_AXIS_TEXT,
            font: { size: 10 },
          },
          grid: {
            color: DEFAULT_GRID_COLOR,
          },
        },
      },

      onHover: (_event: any, elements: any) => {
        if (forceLatestRef.current) return;

        const now =
          typeof performance !== "undefined" &&
          typeof performance.now === "function"
            ? performance.now()
            : Date.now();
        if (now < (suppressHoverUntilMsRef.current ?? 0)) return;

        const next =
          elements &&
          elements.length > 0 &&
          typeof elements[0]?.index === "number"
            ? (elements[0].index as number)
            : null;

        pendingHoverIdxRef.current = next;

        if (typeof window === "undefined") return;

        const commit = () => {
          if (hoverRafRef.current !== null) return;
          hoverRafRef.current = window.requestAnimationFrame(() => {
            hoverRafRef.current = null;
            lastHoverCommitMsRef.current =
              typeof performance !== "undefined" &&
              typeof performance.now === "function"
                ? performance.now()
                : Date.now();
            const scheduled = pendingHoverIdxRef.current;
            setHoveredIdx((prev) => (prev === scheduled ? prev : scheduled));
          });
        };

        const elapsed = now - lastHoverCommitMsRef.current;
        if (elapsed >= HOVER_THROTTLE_MS) {
          commit();
          return;
        }

        if (hoverThrottleTimeoutRef.current !== null) return;
        const wait = Math.max(0, HOVER_THROTTLE_MS - elapsed);
        hoverThrottleTimeoutRef.current = window.setTimeout(() => {
          hoverThrottleTimeoutRef.current = null;
          commit();
        }, wait);
      },
    };
  }, [
    bitrateYMax,
    forceLatestRef,
    hoverRafRef,
    isRealtime,
    pendingHoverIdxRef,
    suppressHoverUntilMsRef,
    realtimeAxis,
    periodAxisSafe,
    setHoveredIdx,
    showDateOnXTicks,
    stepSize,
  ]);

  const chartDataList = useMemo(() => {
    return chartConfigs.map((cfg) => {
      const data = chartPoints.map((p) => ({
        x: p.xMs,
        y: getSeriesY(p, cfg.key),
      }));

      const borderColor = cfg.color;
      const backgroundColor = hexToRgba(cfg.color, 0.12);

      return {
        datasets: [
          {
            label: cfg.name,
            data,
            borderColor,
            backgroundColor,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 10,
            fill: true,
            spanGaps: false,
          },
        ],
      };
    });
  }, [chartConfigs, chartPoints, getSeriesY]);

  return { bitrateChartOptions, chartDataList };
};
